import { ReactComponent as CarouselArrowLeftIcon } from "assets/icons/carousel-arrow-left.svg";
import { ReactComponent as CarouselArrowRightIcon } from "assets/icons/carousel-arrow-right.svg";
import { classNames } from "helpers/ClassNameHelper";
import { throttle } from "lodash";
import { Module, modules } from "modules/common";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { RootState } from "store/store";
import { AccessibilityContext } from "views/AccessibilityContext";
import { Button } from "../Button";

const MenuModuleItem: React.FC<{ module: Module; lowVision?: boolean }> = ({
    module,
    lowVision,
}) => {
    const itemClass = classNames("nav-item", lowVision && "low-vision");

    return module.route ? (
        <NavLink key={module.id} className={itemClass} to={module.route ?? "#"}>
            {module.icon && <img src={module.icon} alt={module.label} className="nav-icon" />}
            <p>{module.label}</p>
        </NavLink>
    ) : (
        <a
            key={module.id}
            href={module.externalUrl}
            className={itemClass}
            target="_blank"
            rel="noopener noreferrer"
        >
            {module.icon && <img src={module.icon} alt={module.label} className="nav-icon" />}
            <p>{module.label}</p>
        </a>
    );
};

const ITEM_WIDTH = 205;
const GAP = 10;
const VISIBLE_ITEMS = 1;

export const HomeModulesMenu = () => {
    const user = useSelector((state: RootState) => state.signIn?.user);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { profile } = useSelector((state: RootState) => state.profile);
    const roleType = user?.role?.value ?? profile?.roleType?.value;

    const [accessibleModules, setAccessibleModules] = useState<Module[]>([]);
    const { lowVision } = useContext(AccessibilityContext);

    useEffect(() => {
        const allModules = modules(lowVision ?? false);
        const activeModuleIds = new Set(profile?.moduleName ?? []);
        const hasActiveSelection = activeModuleIds.size > 0;

        const checkAccessible = (module: Module) =>
            !module.headerOnly &&
            module.roles.includes(roleType ?? 0) &&
            !module.disabledForRelease &&
            (!hasActiveSelection || activeModuleIds.has(module.id));

        const modulesAccessible = allModules.flatMap(module => {
            if (module.headerOnly) return [];

            if (module.children) {
                return module.children.filter(checkAccessible);
            }

            return checkAccessible(module) ? [module] : [];
        });

        setAccessibleModules(modulesAccessible);
    }, [roleType, lowVision, profile?.moduleName]);

    useEffect(() => {
        if (scrollContainerRef.current && accessibleModules.length > 0) {
            scrollContainerRef.current.style.setProperty(
                "--nav-items-count",
                accessibleModules.length.toString()
            );
        }
    }, [accessibleModules]);

    const showArrows = accessibleModules.length > VISIBLE_ITEMS;

    const scroll = useMemo(
        () =>
            throttle((isLeft: boolean) => {
                if (!scrollContainerRef.current) return;

                const scrollAmount = (ITEM_WIDTH + GAP) * VISIBLE_ITEMS;
                const offset = isLeft ? -scrollAmount : scrollAmount;

                scrollContainerRef.current.scrollBy({
                    left: offset,
                    behavior: "smooth",
                });
            }, 400),
        []
    );

    useEffect(() => {
        return () => {
            scroll.cancel();
        };
    }, [scroll]);

    return (
        <div className={classNames("scrollable-nav", lowVision && "low-vision")}>
            {showArrows && (
                <Button
                    className={classNames("nav-arrow left", lowVision && "low-vision")}
                    onClick={() => scroll(true)}
                    size="small"
                    disabled={false}
                >
                    <CarouselArrowLeftIcon />
                </Button>
            )}
            <div className="nav-container" ref={scrollContainerRef}>
                <div className={classNames("nav-items", lowVision && "low-vision")}>
                    {accessibleModules.map(module => (
                        <MenuModuleItem key={module.id} module={module} lowVision={lowVision} />
                    ))}
                </div>
            </div>
            {showArrows && (
                <Button
                    className={classNames("nav-arrow right", lowVision && "low-vision")}
                    onClick={() => scroll(false)}
                    size="small"
                    disabled={false}
                >
                    <CarouselArrowRightIcon />
                </Button>
            )}
        </div>
    );
};

export default HomeModulesMenu;
